import { MenuItem, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-input-2';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { InternalRoutes } from '../../../../enum/InternalRoutes';
import ToggleSwitch from '../../../../shared/components/ToggleSwitch';
import { useSearchQuery } from '../../../../shared/hooks/useSearchQuery';
import { setRegisterUser } from '../../../../store/register/influencer/influencerRegistrationSlice';
import { IAllStates } from '../../../../store/rootReducer';
import { ErrorToast, SuccessToast } from '../../../../utils/toasters';
import { AuthService } from '../../../service';
import { StepperButtons } from '../../brand/components/StepperButtons';
import { IInfluencerBillingRequest } from '../dto/IInfluencerBillingRequest';
import { getSafeValue, validateVAT } from '../../../../shared/functions/Functions';
import { Contact } from '../../../../model/shared/Contact';
import { Bank } from '../../../../model/shared/Bank';
import { FiInfo } from 'react-icons/fi';
import { IRegisterStepperProps } from '../../../../model/shared/IRegistration';

export function InfluencerStep6({ handleBackStep, stepIdNumber }: IRegisterStepperProps) {
    const influencerData = useSelector((state: IAllStates) => state.influencerRegistration.user);
    const [billingInfo, setBillingInfo] = useState<IInfluencerBillingRequest>({
        contact: influencerData?.contact as Contact,
        bank: influencerData?.userBankAccount?.bank as Bank,
        verificationUUID: influencerData?.verificationUUID ?? 0,
    });
    const [vatError, setVatError] = useState(false);
    const { country } = useSelector((states: IAllStates) => states.enums);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const token = getSafeValue(useSearchQuery('token'));

    useEffect(() => {
        if (!token) {
            navigate('/');
            return;
        }
        AuthService.getInfluencerData(token)
            .then(response => {
                dispatch(setRegisterUser(response));
                setBillingInfo(prev => ({
                    ...prev,
                    bank: {
                        ...response?.userBankAccount?.bank,
                        accountPhone: response?.userBankAccount?.bank?.accountPhone ?? response?.contact?.phone ?? '',
                        accountCountry: response?.userBankAccount?.bank?.accountCountry ?? '',
                    },
                    contact: {
                        ...response?.contact,
                        invoiceFirstName: response?.contact?.invoiceFirstName ?? response?.firstName,
                        invoiceLastName: response?.contact?.invoiceLastName ?? response?.lastName,
                        email: response?.contact?.email ?? response?.email,
                        smallBusiness:
                            response?.userBankAccount?.bank?.accountCountry === 'AT'
                                ? !!response?.contact?.smallBusiness
                                : false,
                        soleProprietor:
                            response?.userBankAccount?.bank?.accountCountry === 'AT'
                                ? false
                                : !!response?.contact?.soleProprietor,
                    },
                }));
            })
            .catch(error => ErrorToast(error));
    }, []);

    const handleNextStep = () => {
        AuthService.billingInfoInfluencer(billingInfo)
            .then(() => {
                navigate(`${InternalRoutes.INFLUENCER_REGISTRATION}/${stepIdNumber + 1}?token=${token}`);
                SuccessToast(t('auth.register.brand.step5.success'));
            })
            .catch(error => ErrorToast(error));
    };
    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, isBank = false) => {
        const { name, value } = event.target;
        setBillingInfo(prev =>
            isBank
                ? {
                      ...prev,
                      bank: {
                          ...prev.bank,
                          [name]: value,
                      },
                      ...(name === 'accountCountry' && {
                          contact: {
                              ...prev.contact,
                              smallBusiness: value === 'AT' ? !!prev.contact?.smallBusiness : false,
                              soleProprietor: value === 'AT' ? false : !!prev.contact?.soleProprietor,
                          },
                      }),
                  }
                : {
                      ...prev,
                      contact: {
                          ...prev.contact,
                          [name]: value,
                      },
                  }
        );
        setVatError(false);
    };
    const changePhoneHandler = (value: string) => {
        setBillingInfo(prev => ({
            ...prev,
            bank: {
                ...prev?.bank,
                accountPhone: value,
            },
        }));
        setVatError(false);
    };

    function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (
            !billingInfo.contact.smallBusiness &&
            !billingInfo.contact.soleProprietor &&
            !validateVAT(billingInfo.contact.uidNumber as unknown as string)
        ) {
            setVatError(true);
            return;
        }
        handleNextStep();
    }

    return (
        <Form onSubmit={e => onSubmit(e)}>
            <Row className="text-start">
                <Col xs={12}>
                    <h5 className="mt-3">{t('auth.register.influencer.step6.receiver')}</h5>
                </Col>
                <Col md={6}>
                    <TextField
                        label={t('auth.register.influencer.step6.firstName')}
                        variant="outlined"
                        name="invoiceFirstName"
                        value={billingInfo?.contact?.invoiceFirstName ?? ''}
                        onChange={changeHandler}
                        size="small"
                        required
                        className="w-100 my-2"
                    />
                </Col>
                <Col md={6}>
                    <TextField
                        label={t('auth.register.influencer.step6.lastName')}
                        variant="outlined"
                        size="small"
                        value={billingInfo?.contact?.invoiceLastName ?? ''}
                        onChange={changeHandler}
                        name="invoiceLastName"
                        required
                        className="w-100 my-2"
                    />
                </Col>
                <Col md={12}>
                    <TextField
                        label={t('auth.register.influencer.step6.company_name')}
                        variant="outlined"
                        size="small"
                        value={billingInfo?.contact?.companyName ?? ''}
                        onChange={changeHandler}
                        name="companyName"
                        required
                        className="w-100 my-2"
                    />
                </Col>
                <Col md={6}>
                    <PhoneInput
                        country="de"
                        placeholder={t('auth.register.brand.step2.phoneNumber')}
                        value={billingInfo?.bank?.accountPhone ?? ''}
                        onChange={changePhoneHandler}
                        inputClass="w-100 my-2"
                    />
                </Col>
                <Col md={6}>
                    <TextField
                        label="E-mail"
                        variant="outlined"
                        value={billingInfo?.contact?.email ?? ''}
                        onChange={changeHandler}
                        size="small"
                        name="email"
                        required
                        type="email"
                        className="w-100 my-2"
                    />
                </Col>
                <Col xs={12}>
                    <h5 className="mt-3">{t('auth.register.influencer.step6.address')}</h5>
                </Col>
                <Col md={12}>
                    <TextField
                        label={t('auth.register.influencer.step6.street')}
                        name="street"
                        variant="outlined"
                        required
                        size="small"
                        value={billingInfo?.contact?.street ?? ''}
                        onChange={changeHandler}
                        className="w-100 my-2"
                    />
                </Col>
                <Col md={6}>
                    <TextField
                        label={t('auth.register.influencer.step6.city')}
                        name="city"
                        value={billingInfo?.contact?.city ?? ''}
                        onChange={changeHandler}
                        variant="outlined"
                        required
                        size="small"
                        className="w-100 my-2"
                    />
                </Col>
                <Col md={6}>
                    <TextField
                        label={t('auth.register.influencer.step6.postalCode')}
                        variant="outlined"
                        name="zip"
                        value={billingInfo?.contact?.zip ?? ''}
                        onChange={changeHandler}
                        size="small"
                        required
                        className="w-100 my-2"
                    />
                </Col>
                <Col md={6}>
                    <TextField
                        label={t('auth.register.influencer.step6.state')}
                        value={billingInfo?.contact?.province ?? ''}
                        onChange={changeHandler}
                        name="province"
                        variant="outlined"
                        required
                        size="small"
                        className="w-100 my-2"
                    />
                </Col>
                <Col className="mt-2" md={6}>
                    <TextField
                        value={billingInfo?.bank?.accountCountry}
                        onChange={e => changeHandler(e, true)}
                        size="small"
                        name="accountCountry"
                        required
                        label={t('general.address.country')}
                        select
                        className="text-start"
                        variant="outlined"
                        fullWidth
                    >
                        {country.map(countryName => (
                            <MenuItem key={countryName} value={countryName}>
                                {t(`general.countries.${countryName}`)}
                            </MenuItem>
                        ))}
                    </TextField>
                </Col>
                <Col xs={12}>
                    <h5 className="mt-3">{t('auth.register.influencer.step6.payment_info')}</h5>
                </Col>
                <Col md={6}>
                    <TextField
                        label={t('auth.register.influencer.step6.bank_name')}
                        onChange={event => changeHandler(event, true)}
                        name="accountBankName"
                        value={billingInfo?.bank?.accountBankName ?? ''}
                        variant="outlined"
                        required
                        size="small"
                        className="w-100 my-2"
                    />
                </Col>
                <Col md={6}>
                    <TextField
                        label={t('auth.register.influencer.step6.IBAN')}
                        onChange={event => changeHandler(event, true)}
                        name="accountIban"
                        value={billingInfo?.bank?.accountIban ?? ''}
                        variant="outlined"
                        required
                        size="small"
                        className="w-100 my-2"
                    />
                </Col>
                <Col md={6}>
                    <TextField
                        label={t('auth.register.influencer.step6.SWIFT')}
                        onChange={event => changeHandler(event, true)}
                        name="accountSwift"
                        value={billingInfo?.bank?.accountSwift ?? ''}
                        variant="outlined"
                        size="small"
                        className="w-100 my-2"
                    />
                </Col>
                <Col md={6} className="d-flex align-items-center">
                    <TextField
                        label={t('auth.register.influencer.step6.VAT')}
                        onChange={changeHandler}
                        value={billingInfo?.contact?.uidNumber}
                        name="uidNumber"
                        variant="outlined"
                        size="small"
                        className="w-100 my-2"
                        required={!billingInfo?.contact?.smallBusiness && !billingInfo?.contact?.soleProprietor}
                        error={vatError}
                        helperText={vatError ? 'Incorrect input' : ''}
                    />
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip id="vat-tooltip">
                                {t(`brand.account.billing_information_modal.vat_tooltip`)}
                            </Tooltip>
                        }
                    >
                        <span>
                            <FiInfo size={14} className="cursor-pointer ml-1" />
                        </span>
                    </OverlayTrigger>
                </Col>
                <Col xs={12}>
                    <h5 className="mt-3">{t('auth.register.influencer.step6.value_text')}</h5>
                </Col>
                {billingInfo?.bank?.accountCountry === 'AT' && (
                    <Col md={6}>
                        <div className="d-flex justify-content-between align-items-center mt-2">
                            <label className="mb-0" htmlFor="smallBusiness">
                                {t('auth.register.influencer.step6.smallBusinessOwner')}
                            </label>
                            <ToggleSwitch
                                onChange={() =>
                                    setBillingInfo(prev => ({
                                        ...prev,
                                        contact: {
                                            ...prev.contact,
                                            smallBusiness: !prev.contact?.smallBusiness,
                                        },
                                    }))
                                }
                                small
                                id="smallBusiness"
                                checked={!!billingInfo?.contact?.smallBusiness}
                            />
                        </div>
                        <span className="text-muted">
                            {t('auth.register.brand.step4.smallBusinessOwnerDescription')}
                        </span>
                    </Col>
                )}
                {billingInfo?.bank?.accountCountry !== 'AT' && (
                    <Col md={6}>
                        <div className="d-flex justify-content-between align-items-center mt-2">
                            <label className="mb-0" htmlFor="soleProprietor">
                                {t('auth.register.influencer.step6.sole_proprietor')}
                            </label>
                            <ToggleSwitch
                                onChange={() =>
                                    setBillingInfo(prev => ({
                                        ...prev,
                                        contact: {
                                            ...prev.contact,
                                            soleProprietor: !prev.contact?.soleProprietor,
                                        },
                                    }))
                                }
                                small
                                id="soleProprietor"
                                checked={!!billingInfo?.contact?.soleProprietor}
                            />
                        </div>
                        <span className="text-muted">{t('auth.register.influencer.step6.sole_proprietor_desc')}</span>
                    </Col>
                )}
                <div className="col-12 mt-4">
                    <StepperButtons
                        currentStep={stepIdNumber - 1}
                        handleNextStep={handleNextStep}
                        handleBackStep={handleBackStep}
                    />
                </div>
            </Row>
        </Form>
    );
}
